/**
 * Cache partition key.
 *
 * The persisted query cache must never leak one context's rows into another, so
 * both the IndexedDB storage key and the persister `buster` embed a partition
 * key derived from the active identity: a fingerprint of the auth token plus the
 * active organisation and partner UUIDs. Switching org/partner (or user) yields
 * a different key, so a different physical IndexedDB entry is read/written and
 * the buster no longer matches the outgoing context's blob.
 *
 * The raw token is sensitive, so only a cheap non-cryptographic fingerprint of
 * it is used - never the token itself.
 */

import { getAuthToken } from '@/shared/lib/auth-token'

function fingerprint(input: string): string {
  let hash = 0
  for (let i = 0; i < input.length; i++) {
    hash = (Math.imul(31, hash) + input.charCodeAt(i)) | 0
  }
  return (hash >>> 0).toString(36)
}

export function getPersistencePartitionKey(): string {
  const token = getAuthToken() ?? 'anon'
  let org = 'no-org'
  let partner = 'no-partner'
  try {
    org = sessionStorage.getItem('activeOrganisationUuid') ?? 'no-org'
    partner = sessionStorage.getItem('activePartnerUuid') ?? 'no-partner'
  } catch {
    // sessionStorage blocked; fall back to the defaults.
  }
  return `${fingerprint(token)}:${org}:${partner}`
}
